import { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  Button,
  IconButton,
  Dialog,
  DialogContent,
  TextField,
  Chip,
  useTheme,
  alpha,
} from "@mui/material";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AddIcon from "@mui/icons-material/Add";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ImageIcon from "@mui/icons-material/Image";
import BlockIcon from "@mui/icons-material/Block";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import PersonIcon from "@mui/icons-material/Person";
import PaymentsIcon from "@mui/icons-material/Payments";
import AddPaymentModal from "./AddPaymentModal";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";

const getModeUI = (mode, theme) => ({
  UPI: { color: theme.palette.primary.main, bg: alpha(theme.palette.primary.main, 0.1) },
  Cash: { color: "#047857", bg: "#dcfce7" },
  Card: { color: theme.palette.primary.main, bg: alpha(theme.palette.primary.main, 0.1) },
  "Bank Transfer": { color: "#c2410c", bg: "#ffedd5" },
})[mode] || { color: "#111827", bg: "#e5e7eb" };

export default function PaymentsSummary({
  orderCost: initialOrderCost = 0,
  payments = [],
  onAddPayment,
  onDeletePayment,
  onSaveOrderCost,
}) {
  const [openReceipt, setOpenReceipt] = useState(false);
  const [activeReceipt, setActiveReceipt] = useState(null);
  const [editingOrder, setEditingOrder] = useState(false);
  const [orderCost, setOrderCost] = useState(initialOrderCost);
  const theme = useTheme();
  const [openAddPayment, setOpenAddPayment] = useState(false);

  const received = payments.reduce((s, p) => s + (p.amount || 0), 0);
  const balance = orderCost - received;

  const getModeIcon = (mode) => {
    if (mode === "UPI") return <PaymentsIcon />;
    if (mode === "Cash") return <CurrencyRupeeIcon />;
    if (mode === "Card") return <CreditCardIcon />;
    if (mode === "Bank Transfer") return <AccountBalanceIcon />;
    return <PaymentsIcon />;
  };

  useEffect(() => {
    setOrderCost(initialOrderCost || 0);
  }, [initialOrderCost]);

  const PaymentItem = ({ p }) => {
    const ui = getModeUI(p.mode, theme);
    const ReceiptIcon = p.receipt?.endsWith(".pdf") ? ReceiptLongIcon : ImageIcon;
    const employeeName = p.employee_name || null;

    const receiptUrl = p.receipt
      ? p.receipt.startsWith("http")
        ? p.receipt
        : `${API_BASE}${p.receipt}`
      : null;

    return (
      <Box
        sx={{
          p: 2,
          borderRadius: 3,
          bgcolor: "#ffffff",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Avatar
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
            borderRadius: 2,
            width: 40,
            height: 40,
          }}
        >
          {getModeIcon(p.mode)}
        </Avatar>

        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography
              component="span"
              sx={{ fontSize: 11, fontWeight: 600, color: theme.palette.primary.main }}
            >
              ₹
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#111827" }}>
              {p.amount}
            </Typography>
          </Box>

          <Typography sx={{ fontSize: 12, color: "#6b7280", fontWeight: 500 }}>
            {p.mode} • {new Date(p.created_at).toLocaleString("en-IN", {
              timeZone: "Asia/Kolkata",
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })}
          </Typography>

          {employeeName && (
            <Chip
              icon={<PersonIcon sx={{ fontSize: 14 }} />}
              label={employeeName}
              size="small"
              sx={{
                mt: 0.75,
                height: 24,
                bgcolor: alpha(theme.palette.text.secondary, 0.08),
                color: "#4b5563",
                fontWeight: 600,
                fontSize: 11,
                "& .MuiChip-icon": { color: "#6b7280" },
              }}
            />
          )}
        </Box>

        <IconButton
          size="small"
          disabled={!p.receipt}
          onClick={() => {
            setActiveReceipt(receiptUrl);
            setOpenReceipt(true);
          }}
          sx={{
            color: p.receipt ? theme.palette.primary.main : "#d1d5db",
            "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.1) },
          }}
        >
          {p.receipt ? <ReceiptIcon fontSize="small" /> : <BlockIcon fontSize="small" />}
        </IconButton>

        <IconButton
          size="small"
          onClick={() => onDeletePayment?.(p._id)}
          sx={{
            color: "#ef4444",
            "&:hover": { bgcolor: alpha("#ef4444", 0.1) },
          }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  return (
    <>
      <Card
        sx={{
          borderRadius: 3,
          bgcolor: "#ffffff",
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2.5,
            }}
          >
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 700,
                color: theme.palette.primary.main,
              }}
            >
              Payments
            </Typography>
            <Button
              size="small"
              variant="text"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={() => setOpenAddPayment(true)}
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 600,
                fontSize: 12,
                textTransform: "uppercase",
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              Add Payment
            </Button>
          </Box>

          {/* Order Cost */}
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 1.5,
            }}
          >
            <Avatar
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
                width: 36,
                height: 36,
                borderRadius: 2,
              }}
            >
              <ShoppingBagIcon sx={{ fontSize: 20 }} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{ fontSize: 10, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.5 }}
              >
                Order Cost
              </Typography>
              {editingOrder ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TextField
                    type="number"
                    size="small"
                    value={orderCost}
                    onChange={(e) => setOrderCost(+e.target.value)}
                    sx={{ width: 100, "& input": { py: 0.5, fontSize: 14 } }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditingOrder(false);
                      onSaveOrderCost?.(orderCost);
                    }}
                    sx={{ color: theme.palette.primary.main }}
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Typography
                  sx={{ fontSize: 18, fontWeight: 700, color: "#111827", cursor: "pointer" }}
                  onClick={() => setEditingOrder(true)}
                >
                  ₹{orderCost}
                </Typography>
              )}
            </Box>
            {!editingOrder && (
              <CheckCircleIcon sx={{ color: "#22c55e", fontSize: 22 }} />
            )}
          </Box>

          {/* Balance */}
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.grey[500], 0.06),
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 2,
            }}
          >
            <Avatar
              sx={{
                bgcolor: alpha(theme.palette.grey[500], 0.12),
                color: "#6b7280",
                width: 36,
                height: 36,
                borderRadius: 2,
              }}
            >
              <AccountBalanceWalletIcon sx={{ fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography
                sx={{ fontSize: 10, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.5 }}
              >
                Balance
              </Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>
                ₹{balance}
              </Typography>
            </Box>
          </Box>

          {/* Payment Items */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {payments.map((p, i) => (
              <PaymentItem key={p._id || i} p={p} />
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Receipt Preview Dialog */}
      <Dialog open={openReceipt} onClose={() => setOpenReceipt(false)} fullWidth>
        <DialogContent sx={{ p: 0 }}>
          {activeReceipt?.endsWith(".pdf") ? (
            <iframe src={activeReceipt} width="100%" height="500" title="Receipt" />
          ) : (
            <Box component="img" src={activeReceipt} sx={{ width: "100%" }} />
          )}
        </DialogContent>
      </Dialog>

      {/* Add Payment Modal */}
      <AddPaymentModal
        open={openAddPayment}
        onClose={() => setOpenAddPayment(false)}
        onSave={async (fd) => {
          await onAddPayment(fd);
          setOpenAddPayment(false);
        }}
      />
    </>
  );
}
